import React, { useEffect, useState } from 'react';
import { X, User, Activity, AlertTriangle, FileText, MessageSquare, Heart, Key, ShieldCheck, ExternalLink } from 'lucide-react';
import { adminAPI } from '../../utils/api';
import AdminNoteModal from './AdminNoteModal';

// 보건복지부 면허민원 — 문서확인번호 진위확인 페이지
const MOHW_DOC_VERIFY_URL = 'https://lic.mohw.go.kr';

// PG(camelCase)/SQLite(snake_case) dual-read 헬퍼
const uf = (u: any, snake: string, camel: string): any => {
  if (!u) return undefined;
  return u[snake] !== undefined ? u[snake] : u[camel];
};

interface DocumentCheck {
  documentNumber: string | null;
  formatValid: boolean;
  issueDate: string | null;
  expiresAt: string | null;
  daysRemaining: number | null;
  withinValidityWindow: boolean | null;
  authentic: boolean;
  reason: string;
}

interface UserDetail {
  user: {
    id: number;
    email: string;
    name: string;
    hospital?: string;
    specialization?: string;
    isVerifiedDoctor: boolean;
    created_at: string;
    [key: string]: any; // 면허 검증 컬럼(dual-read)
  };
  stats: {
    postsCount: number;
    commentsCount: number;
    likesReceived: number;
    apiKeysCount: number;
  };
  recentActivity: Array<{
    type: 'post' | 'comment';
    id: number;
    content: string;
    createdAt: string;
  }>;
  reportsReceived: number;
  reportsMade: number;
  documentCheck?: DocumentCheck;
  specialistDocumentCheck?: DocumentCheck;
}

interface UserDetailModalProps {
  userId: number;
  onClose: () => void;
  onAction?: () => void;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({ userId, onClose, onAction }) => {
  const [detail, setDetail] = useState<UserDetail | null>(null);
  // 회원 삭제(탈퇴 요청 처리) — 대상 이메일을 그대로 입력해야 실행된다
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [attempts, setAttempts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  // 관리자 메모 입력 모달 (window.prompt 대체)
  const [noteModal, setNoteModal] = useState<null | 'verify' | 'reject'>(null);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUserDetail(userId);
      setDetail(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || '사용자 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttempts = async () => {
    try {
      const res = await adminAPI.getVerificationAttempts(userId);
      setAttempts(res.data.attempts || []);
    } catch {
      // 감사 이력은 부가 정보이므로 실패해도 모달은 동작
      setAttempts([]);
    }
  };

  useEffect(() => {
    fetchDetail();
    fetchAttempts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const refresh = async () => {
    await Promise.all([fetchDetail(), fetchAttempts()]);
    if (onAction) onAction();
  };

  // 승인 버튼: 이미 검증된 경우 즉시 철회, 아니면 메모 모달을 연다.
  const handleVerify = async () => {
    if (!detail) return;
    if (detail.user.isVerifiedDoctor) {
      try {
        setActionLoading(true);
        await adminAPI.unverifyUser(userId);
        alert('검증이 철회되었습니다.');
        await refresh();
      } catch (err: any) {
        alert(err?.response?.data?.error || '작업을 수행할 수 없습니다.');
      } finally {
        setActionLoading(false);
      }
      return;
    }
    setNoteModal('verify');
  };

  const handleReject = () => {
    if (!detail) return;
    setNoteModal('reject');
  };

  // 메모 모달 확인 → 실제 승인/반려 API 호출
  const submitNote = async (note: string) => {
    const mode = noteModal;
    if (!mode) return;
    try {
      setActionLoading(true);
      if (mode === 'verify') {
        await adminAPI.verifyUser(userId, note || undefined);
        alert('검증이 승인되었습니다.');
      } else {
        await adminAPI.rejectUser(userId, note || undefined);
        alert('검증이 반려되었습니다.');
      }
      setNoteModal(null);
      await refresh();
    } catch (err: any) {
      alert(err?.response?.data?.error || '작업을 수행할 수 없습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // 문서확인번호 진위확인 결과 기록/해제 (관리자가 lic.mohw.go.kr에서 직접 확인 후)
  const handleVerifyDocument = async (confirmed: boolean) => {
    try {
      setActionLoading(true);
      await adminAPI.verifyDocument(userId, { confirmed });
      alert(confirmed ? '문서확인번호 진위확인이 기록되었습니다.' : '문서확인번호 진위확인이 해제되었습니다.');
      await refresh();
    } catch (err: any) {
      alert(err?.response?.data?.error || '작업을 수행할 수 없습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <User className="w-5 h-5 mr-2 text-purple-600" />
            👤 사용자 상세 정보
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* 내용 */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          ) : detail ? (
            <div className="space-y-6">
              {/* 기본 정보 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">기본 정보</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">이름:</span>
                    <span className="font-medium">{detail.user.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">이메일:</span>
                    <span className="font-medium">{detail.user.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">병원:</span>
                    <span className="font-medium">{detail.user.hospital || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">전문과:</span>
                    <span className="font-medium">{detail.user.specialization || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">가입일:</span>
                    <span className="font-medium">
                      {new Date(detail.user.created_at).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">검증 상태:</span>
                    <span className={`font-medium ${detail.user.isVerifiedDoctor ? 'text-green-600' : 'text-yellow-600'}`}>
                      {detail.user.isVerifiedDoctor ? '✓ 검증됨' : '⏳ 대기 중'}
                    </span>
                  </div>
                </div>
              </div>

              {/* 활동 통계 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-purple-600" />
                  활동 통계
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="text-2xl font-bold text-blue-900">{detail.stats.postsCount}</span>
                    </div>
                    <p className="text-sm text-blue-700 mt-1">작성한 게시글</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                      <span className="text-2xl font-bold text-green-900">{detail.stats.commentsCount}</span>
                    </div>
                    <p className="text-sm text-green-700 mt-1">작성한 댓글</p>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-4 border border-pink-200">
                    <div className="flex items-center justify-between">
                      <Heart className="w-5 h-5 text-pink-600" />
                      <span className="text-2xl font-bold text-pink-900">{detail.stats.likesReceived}</span>
                    </div>
                    <p className="text-sm text-pink-700 mt-1">받은 좋아요</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <div className="flex items-center justify-between">
                      <Key className="w-5 h-5 text-yellow-600" />
                      <span className="text-2xl font-bold text-yellow-900">{detail.stats.apiKeysCount}</span>
                    </div>
                    <p className="text-sm text-yellow-700 mt-1">API 키</p>
                  </div>
                </div>
              </div>

              {/* 최근 활동 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">최근 활동</h3>
                <div className="space-y-2">
                  {detail.recentActivity.length > 0 ? (
                    detail.recentActivity.map((activity, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            activity.type === 'post' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {activity.type === 'post' ? '게시글' : '댓글'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(activity.createdAt).toLocaleString('ko-KR')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 truncate">
                          {activity.content.substring(0, 100)}
                          {activity.content.length > 100 && '...'}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">최근 활동이 없습니다.</p>
                  )}
                </div>
              </div>

              {/* 신고 이력 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
                  신고 이력
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">받은 신고:</span>
                    <span className={`font-medium ${detail.reportsReceived > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {detail.reportsReceived}건
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">한 신고:</span>
                    <span className="font-medium">{detail.reportsMade}건</span>
                  </div>
                </div>
              </div>

              {/* 면허 검증 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Key className="w-5 h-5 mr-2 text-purple-600" />
                  면허 검증
                </h3>

                {/* 검증 메타 정보 */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">검증 상태:</span>
                    <span className="font-medium">
                      {(() => {
                        const s = uf(detail.user, 'verification_status', 'verificationStatus') || (detail.user.isVerifiedDoctor ? 'verified' : 'none');
                        const map: Record<string, { label: string; cls: string }> = {
                          verified: { label: '✓ 검증됨', cls: 'text-green-600' },
                          pending: { label: '⏳ 검토 대기', cls: 'text-yellow-600' },
                          rejected: { label: '✕ 반려됨', cls: 'text-red-600' },
                          none: { label: '— 미신청', cls: 'text-gray-500' },
                        };
                        const v = map[s] || map.none;
                        return <span className={v.cls}>{v.label}</span>;
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">검증 방법:</span>
                    <span className="font-medium">{uf(detail.user, 'verification_method', 'verificationMethod') || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">신뢰도 점수:</span>
                    <span className="font-medium">
                      {(() => {
                        const score = uf(detail.user, 'verification_score', 'verificationScore');
                        return score !== undefined && score !== null ? `${score}/100` : 'N/A';
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">의사 면허번호:</span>
                    <span className="font-medium">{uf(detail.user, 'doctor_license_number', 'doctorLicenseNumber') || 'N/A'}</span>
                  </div>
                  {(uf(detail.user, 'doctor_license_document_number', 'doctorLicenseDocumentNumber')) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">문서확인번호:</span>
                      <span className="font-medium">{uf(detail.user, 'doctor_license_document_number', 'doctorLicenseDocumentNumber')}</span>
                    </div>
                  )}
                  {(uf(detail.user, 'specialist_license_number', 'specialistLicenseNumber')) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">전문의 자격번호:</span>
                      <span className="font-medium">{uf(detail.user, 'specialist_license_number', 'specialistLicenseNumber')}</span>
                    </div>
                  )}
                  {(uf(detail.user, 'verification_notes', 'verificationNotes')) && (
                    <div className="pt-2 border-t border-gray-200">
                      <span className="text-gray-600 block mb-1">메모:</span>
                      <span className="text-sm text-gray-800">{uf(detail.user, 'verification_notes', 'verificationNotes')}</span>
                    </div>
                  )}
                </div>

                {/* 문서확인번호 진위확인 (관리자 보조) */}
                {(() => {
                  const dc = detail.documentCheck;
                  if (!dc || !dc.documentNumber) return null;
                  const windowCls =
                    dc.withinValidityWindow === false
                      ? 'text-red-600'
                      : dc.withinValidityWindow === true
                      ? 'text-green-600'
                      : 'text-gray-500';
                  return (
                    <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200 mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-indigo-900 flex items-center">
                          <ShieldCheck className="w-4 h-4 mr-1.5" />
                          문서확인번호 진위확인
                        </h4>
                        {dc.authentic ? (
                          <span className="text-xs font-medium px-2 py-1 rounded bg-green-100 text-green-700">
                            ✓ 진위확인 완료
                          </span>
                        ) : (
                          <span className="text-xs font-medium px-2 py-1 rounded bg-yellow-100 text-yellow-700">
                            진위확인 대기
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">문서확인번호:</span>
                          <span className="font-medium">{dc.documentNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">발급일:</span>
                          <span className="font-medium">{dc.issueDate || '미상'}</span>
                        </div>
                        {dc.expiresAt && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">유효기간(발급 90일):</span>
                            <span className={`font-medium ${windowCls}`}>
                              {dc.expiresAt}
                              {dc.daysRemaining !== null &&
                                (dc.daysRemaining >= 0
                                  ? ` (D-${dc.daysRemaining})`
                                  : ` (${-dc.daysRemaining}일 경과)`)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">상태:</span>
                          <span className={`font-medium ${windowCls}`}>{dc.reason}</span>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <a
                          href={MOHW_DOC_VERIFY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-xs font-medium px-3 py-1.5 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-100 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1" />
                          보건복지부 면허민원 확인
                        </a>
                        {dc.authentic ? (
                          <button
                            onClick={() => handleVerifyDocument(false)}
                            disabled={actionLoading}
                            className="text-xs font-medium px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                          >
                            진위확인 해제
                          </button>
                        ) : (
                          <button
                            onClick={() => handleVerifyDocument(true)}
                            disabled={actionLoading || !dc.formatValid}
                            className="text-xs font-medium px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={!dc.formatValid ? '문서확인번호 형식 오류(12자리 아님)' : ''}
                          >
                            문서확인 완료 기록
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* 면허 이미지 미리보기 */}
                {(() => {
                  const docUrl = uf(detail.user, 'doctor_license_url', 'doctorLicenseUrl');
                  const specUrl = uf(detail.user, 'specialist_license_url', 'specialistLicenseUrl');
                  if (!docUrl && !specUrl) {
                    const vstatus = uf(detail.user, 'verification_status', 'verificationStatus');
                    const terminal = vstatus === 'verified' || vstatus === 'rejected';
                    return (
                      <p className="text-gray-500 text-sm">
                        {terminal
                          ? '면허 이미지가 보관 정책에 따라 삭제되었습니다(PII 최소화).'
                          : '업로드된 면허 이미지가 없습니다.'}
                      </p>
                    );
                  }
                  return (
                    <div className="grid grid-cols-2 gap-3">
                      {docUrl && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">의사 면허증</p>
                          <a href={docUrl} target="_blank" rel="noopener noreferrer">
                            <img src={docUrl} alt="의사 면허증" className="w-full rounded-lg border border-gray-200 object-cover" />
                          </a>
                        </div>
                      )}
                      {specUrl && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">전문의 자격증</p>
                          <a href={specUrl} target="_blank" rel="noopener noreferrer">
                            <img src={specUrl} alt="전문의 자격증" className="w-full rounded-lg border border-gray-200 object-cover" />
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* 검증 시도 타임라인 */}
                {attempts.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">검증 이력</h4>
                    <div className="space-y-2">
                      {attempts.map((a, idx) => {
                        const result = a.result || a.RESULT;
                        const resultMap: Record<string, { label: string; cls: string }> = {
                          auto_verified: { label: '자동 검증', cls: 'bg-green-100 text-green-700' },
                          manual: { label: '수동 승인', cls: 'bg-green-100 text-green-700' },
                          pending: { label: '검토 대기', cls: 'bg-yellow-100 text-yellow-700' },
                          duplicate: { label: '중복 면허', cls: 'bg-orange-100 text-orange-700' },
                          rejected: { label: '반려', cls: 'bg-red-100 text-red-700' },
                          purged: { label: '이미지 정리', cls: 'bg-gray-100 text-gray-600' },
                          error: { label: '오류', cls: 'bg-gray-100 text-gray-700' },
                        };
                        const r = resultMap[result] || { label: result || '기록', cls: 'bg-gray-100 text-gray-700' };
                        const created = a.created_at || a.createdAt;
                        return (
                          <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-medium px-2 py-1 rounded ${r.cls}`}>{r.label}</span>
                              <span className="text-xs text-gray-500">
                                {created ? new Date(created).toLocaleString('ko-KR') : ''}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <span>방법: {a.method || 'N/A'}</span>
                              <span>점수: {a.score !== undefined && a.score !== null ? a.score : 'N/A'}</span>
                            </div>
                            {a.notes && <p className="text-xs text-gray-700 mt-1">{a.notes}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* 회원 삭제 — 처리방침의 "연락처로 탈퇴 요청"을 처리한다. 되돌릴 수 없음 */}
        {detail && (
          <div className="px-6 py-4 border-t border-gray-200 bg-white">
            {!deleteMode ? (
              <button onClick={() => setDeleteMode(true)} className="text-sm text-red-600 hover:text-red-700 underline">
                회원 삭제(탈퇴 요청 처리)
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  계정과 게시물·댓글·파일 등 모든 기록을 즉시 삭제합니다. 확인을 위해 이메일 <strong>{detail.user.email}</strong>을 입력하세요.
                </p>
                <div className="flex gap-2">
                  <input
                    value={deleteEmail}
                    onChange={(e) => setDeleteEmail(e.target.value)}
                    placeholder="회원 이메일"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <button
                    disabled={deleting || deleteEmail.trim().toLowerCase() !== String(detail.user.email).toLowerCase()}
                    onClick={async () => {
                      setDeleting(true);
                      setDeleteError('');
                      try {
                        await adminAPI.deleteUser(userId, deleteEmail.trim());
                        onAction?.();
                        onClose();
                      } catch (e: any) {
                        setDeleteError(e?.response?.data?.error || '삭제하지 못했습니다.');
                      } finally {
                        setDeleting(false);
                      }
                    }}
                    className="px-3 py-2 bg-red-600 text-white rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleting ? '삭제 중' : '삭제'}
                  </button>
                  <button onClick={() => { setDeleteMode(false); setDeleteEmail(''); setDeleteError(''); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
                    취소
                  </button>
                </div>
                {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
              </div>
            )}
          </div>
        )}

        {/* 푸터 - 액션 버튼 */}
        {detail && (
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex space-x-3">
            <button
              onClick={handleVerify}
              disabled={actionLoading}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                detail.user.isVerifiedDoctor
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {actionLoading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  처리 중...
                </span>
              ) : detail.user.isVerifiedDoctor ? (
                '검증 철회'
              ) : (
                '검증 승인'
              )}
            </button>
            {!detail.user.isVerifiedDoctor && (
              <button
                onClick={handleReject}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 rounded-lg font-medium transition-all bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                검증 반려
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-all"
            >
              닫기
            </button>
          </div>
        )}
      </div>

      {/* 관리자 메모 입력 모달 (window.prompt 대체) */}
      <AdminNoteModal
        open={noteModal === 'verify'}
        title="면허 검증 승인"
        description="승인 메모를 남기면 감사 로그에 기록됩니다. 예: 면허 사본 확인 완료"
        placeholder="승인 메모 (선택)"
        confirmLabel="승인"
        confirmTone="primary"
        loading={actionLoading}
        onConfirm={submitNote}
        onCancel={() => setNoteModal(null)}
      />
      <AdminNoteModal
        open={noteModal === 'reject'}
        title="면허 검증 반려"
        description="반려 사유를 입력하세요. 감사 로그에 기록됩니다. 예: 면허번호 불일치"
        placeholder="반려 사유"
        confirmLabel="반려"
        confirmTone="danger"
        requireNote
        loading={actionLoading}
        onConfirm={submitNote}
        onCancel={() => setNoteModal(null)}
      />
    </div>
  );
};

export default UserDetailModal;



